import { useRef, useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

// Define the tab item type
export interface TabItem {
  id: string;
  name: string;
}

interface FoodTabsProps {
  tabs: TabItem[];
  defaultSelected?: string | null;
  onTabChange?: (tabId: string | null) => void;
  maxWidth?: string;
}

export default function FoodTabs({
  tabs = [],
  defaultSelected = null,
  onTabChange,
  maxWidth = "1200px",
}: FoodTabsProps) {
  const [activeTabId, setActiveTabId] = useState<string | null>(
    defaultSelected
  );
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const tabRefs = useRef<Record<string, HTMLButtonElement | null>>({});

  // Initialize tab refs
  useEffect(() => {
    tabs.forEach((tab) => {
      if (!tabRefs.current[tab.id]) {
        tabRefs.current[tab.id] = null;
      }
    });
  }, [tabs]);

  // Scroll selected tab into view when it changes
  useEffect(() => {
    if (activeTabId && tabRefs.current[activeTabId]) {
      scrollTabIntoView(activeTabId);
    }
  }, [activeTabId]);

  const scrollTabIntoView = (tabId: string) => {
    const tabElement = tabRefs.current[tabId];
    if (!tabElement || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    const tabRect = tabElement.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();

    // Check if tab is not fully visible
    const isTabLeftOfView = tabRect.left < containerRect.left + 50; // Adding offset for arrow
    const isTabRightOfView = tabRect.right > containerRect.right - 50; // Adding offset for arrow

    if (isTabLeftOfView) {
      // Scroll so the tab is at the left with some padding
      const scrollLeft =
        container.scrollLeft + (tabRect.left - containerRect.left) - 60;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    } else if (isTabRightOfView) {
      // Scroll so the tab is at the right with some padding
      const scrollLeft =
        container.scrollLeft + (tabRect.right - containerRect.right) + 60;
      container.scrollTo({ left: scrollLeft, behavior: "smooth" });
    }
  };

  const scrollLeft = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: -200, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollBy({ left: 200, behavior: "smooth" });
    }
  };

  const handleTabClick = (tabId: string) => {
    // If clicking the already active tab, deselect it
    if (activeTabId === tabId) {
      setActiveTabId(null);
      if (onTabChange) onTabChange(null);
    } else {
      setActiveTabId(tabId);
      if (onTabChange) onTabChange(tabId);
    }
  };

  return (
    <div className="w-full bg-ekblue">
      <div
        className="relative mx-auto w-full"
        // style={{ maxWidth }}
      >
        <div className="absolute left-0 top-0 bottom-0 z-10 flex items-center">
          <button
            onClick={scrollLeft}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white shadow-md backdrop-blur-sm ml-3"
            aria-label="Scroll left"
          >
            <ChevronLeft className="h-5 w-5" />
          </button>
        </div>

        <div
          ref={scrollContainerRef}
          className="flex w-full overflow-x-auto scrollbar-hide"
          style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
        >
          <div className="flex px-16 py-1.5 md:px-18">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                ref={(el) => {
                  tabRefs.current[tab.id] = el;
                }}
                onClick={() => handleTabClick(tab.id)}
                className={`whitespace-nowrap px-4 py-2 text-sm font-medium transition-all duration-200 rounded-full mx-1 md:px-6 md:text-base
                ${
                  activeTabId === tab.id
                    ? "bg-white text-black font-semibold"
                    : "text-white opacity-90 hover:opacity-100"
                }`}
              >
                {tab.name}
              </button>
            ))}
          </div>
        </div>

        <div className="absolute right-0 top-0 bottom-0 z-10 flex items-center">
          <button
            onClick={scrollRight}
            className="flex h-8 w-8 items-center justify-center rounded-full bg-white/20 text-white shadow-md backdrop-blur-sm mr-3"
            aria-label="Scroll right"
          >
            <ChevronRight className="h-5 w-5" />
          </button>
        </div>
      </div>

      <style jsx global>{`
        /* Hide scrollbar for Chrome, Safari and Opera */
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
}
